import React, { useRef } from "react";

interface SecurityCheckProps {
  onSecureSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onBotDetected?: () => void;
  children: (helpers: {
    honeypotProps: React.InputHTMLAttributes<HTMLInputElement>;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  }) => React.ReactNode;
}

const HONEYPOT_NAME = "website_url"; // neutral name, so autofill ignores it
const MIN_FILL_TIME_MS = 300;       // humans can't submit faster than this

const SecurityCheck: React.FC<SecurityCheckProps> = ({
  onSecureSubmit,
  onBotDetected,
  children,
}) => {
  const humanInteracted = useRef(false);
  const mountedAt = useRef(Date.now());

  // Step 1: any real mouse / touch / keyboard / typing marks the user as human
  const markHuman = (e: React.SyntheticEvent) => {
    if (e.nativeEvent.isTrusted) {
      humanInteracted.current = true;
    }
  };

  // Step 2: hidden honeypot input (works on PC and mobile)
  const honeypotProps: React.InputHTMLAttributes<HTMLInputElement> = {
    name: HONEYPOT_NAME,
    type: "text",
    defaultValue: "",
    autoComplete: "off",
    tabIndex: -1,
    "aria-hidden": true,
    style: {
      position: "absolute",
      left: "-9999px",
      width: "1px",
      height: "1px",
      opacity: 0,
      pointerEvents: "none",
    },
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();


    // Read the honeypot directly from the form (catches autofill too)
    const honeypot = e.currentTarget.elements.namedItem(
      HONEYPOT_NAME
    ) as HTMLInputElement | null;
    const honeypotFilled = !!honeypot && honeypot.value.trim().length > 0;

    const tooFast = Date.now() - mountedAt.current < MIN_FILL_TIME_MS;
    const isBot = honeypotFilled || !humanInteracted.current || tooFast;

    if (isBot) {
      onBotDetected?.();
      return;
    }

    onSecureSubmit(e);
  };

  return (
    <div
      onPointerDown={markHuman}
      onMouseMove={markHuman}
      onTouchStart={markHuman}
      onKeyDown={markHuman}
      onInput={markHuman}
      onFocus={markHuman}
      style={{ position: "relative" }}
    >
      {children({ honeypotProps, handleSubmit })}
    </div>
  );
};

export default SecurityCheck;